import { useState, useMemo, useEffect } from 'react';
import { Check, Copy, Download, Edit2, ExternalLink, Eye, Link2, Paperclip, Plus, Search, Send, Trash2, X } from 'lucide-react';
import { POLICY_TYPES } from '../policyTypes.js';
import { REQUIRED_DOCS_BY_POLICY_DEFAULT } from '../policyDocs.js';
import { listUploadsForRequest, getDocumentReadUrl } from '../api.js';
import { toast } from './Toast.jsx';
import { fmt } from '../utils.js';
import { confirmDialog, ClientSearchSelect } from './ui.jsx';
import { mensajeDeFalloDeSolicitud } from '../motivoDeSolicitud.js';
import { filaDeSolicitud } from '../filaDeSolicitud.js';
import { enviarSmsAlCliente } from '../enviarSms.js';
import { AiDraftButton } from './AiDraftButton.jsx';
import { prefsGet, prefsSet } from '../uiPrefsStore.js';

// Excel via SheetJS (cargado del CDN al pulsar, como en ClientDetail.jsx). El
// boton "Excel" de Solicitudes llamaba a exportToExcel sin tenerlo definido en
// este archivo y no hacia nada mas que lanzar un error.
async function exportToExcel(data, filename, sheetName) {
  const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs');
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName || 'Data');
  XLSX.writeFile(wb, filename);
}

// Standard intro text the client sees (per language). Pre-fills the message box
// so the agent doesn't retype it each time; once edited it's SAVED per language
// (uiPrefs) and reused next time (Lissette 2026-06-18).
const DEFAULT_DOC_MSG = {
  es: '¡Hola! Necesitamos algunos documentos de tu parte. Por favor súbelos con el botón de abajo. ¡Gracias!',
  en: 'Hi! We need a few documents from you. Please upload them using the button below. Thank you!',
};
const loadDocMsg = (l) => {
  try { return prefsGet('deskte:docReqMsg_' + l) || DEFAULT_DOC_MSG[l] || ''; }
  catch { return DEFAULT_DOC_MSG[l] || ''; }
};

// Local calendar date as 'YYYY-MM-DD'. new Date().toISOString() is UTC, so
// after ~8pm in Florida (UTC-4/-5) it rolls to tomorrow — stamping requests
// with the wrong day. Build the date from local getters instead.
const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

function DocTemplatesEditor({ master, onSave, onClose, lang }) {
  const [local, setLocal] = useState(() => JSON.parse(JSON.stringify(master || REQUIRED_DOCS_BY_POLICY_DEFAULT)));
  const [selectedType, setSelectedType] = useState("aca");
  const [trRow, setTrRow] = useState(null); // row index currently translating
  const docs = local[selectedType] || [];
  const addDoc = () => setLocal(prev => ({ ...prev, [selectedType]: [...(prev[selectedType]||[]), {name:"", nameEs:"", required:false}] }));
  const removeDoc = (idx) => setLocal(prev => ({ ...prev, [selectedType]: (prev[selectedType]||[]).filter((_,i)=>i!==idx) }));
  const updateDoc = (idx, field, val) => setLocal(prev => ({ ...prev, [selectedType]: (prev[selectedType]||[]).map((d,i)=>i===idx?{...d,[field]:val}:d) }));
  // Fill the empty-language name from the filled one, via AI (Azure OpenAI).
  const translateName = async (idx) => {
    const d = (local[selectedType]||[])[idx] || {};
    const plan = (d.name||"").trim() ? { text: d.name, fromLang: "en", toLang: "es", field: "nameEs" }
               : (d.nameEs||"").trim() ? { text: d.nameEs, fromLang: "es", toLang: "en", field: "name" }
               : null;
    if (!plan) { toast.error(lang==="es"?"Escribe un nombre primero":"Type a name first"); return; }
    setTrRow(idx);
    try {
      const api = await import("../api.js");
      const { translated } = await api.aiTranslateTemplate({ text: plan.text, fromLang: plan.fromLang, toLang: plan.toLang });
      if (translated && translated.trim()) updateDoc(idx, plan.field, translated.trim());
    } catch (e) {
      toast.error((lang==="es"?"No se pudo traducir: ":"Translation failed: ")+(e.message||"error"));
    } finally { setTrRow(null); }
  };
  const resetType = () => setLocal(prev => ({ ...prev, [selectedType]: [...(REQUIRED_DOCS_BY_POLICY_DEFAULT[selectedType]||[])] }));
  const resetAll = async () => { if (await confirmDialog({ message: lang==="es"?"¿Restaurar todas las plantillas a los valores por defecto?":"Reset all templates to defaults?", danger: true })) setLocal(JSON.parse(JSON.stringify(REQUIRED_DOCS_BY_POLICY_DEFAULT))); };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.7)", backdropFilter:"blur(6px)", zIndex:100, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"var(--c-surface,#0d1c2e)", border:"1px solid var(--c-border2,#1f3f60)", borderRadius:16, width:"100%", maxWidth:800, maxHeight:"90vh", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 24px 80px rgba(0,0,0,.6)" }}>
        <div style={{ padding:"1rem 1.25rem", borderBottom:"1px solid var(--c-border,#1a3550)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"1rem", color:"var(--c-white,#eef6ff)" }}>⚙️ {lang==="es"?"Plantillas de Documentos":"Document Templates"}</div>
            <div style={{ fontSize:".7rem", color:"var(--c-text3,#45688a)", marginTop:2 }}>{lang==="es"?"Define que documentos se piden por cada tipo de poliza":"Define required documents per policy type"}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--c-text3,#45688a)", cursor:"pointer", fontSize:"1.3rem", padding:4 }}>✕</button>
        </div>
        <div style={{ flex:1, display:"flex", minHeight:0, overflow:"hidden" }}>
          <div style={{ width:180, borderRight:"1px solid var(--c-border,#1a3550)", overflowY:"auto", padding:".5rem" }}>
            {POLICY_TYPES.map(pt => {
              const count = (local[pt.id]||[]).length;
              const on = selectedType === pt.id;
              return (
                <button key={pt.id} onClick={()=>setSelectedType(pt.id)} style={{ width:"100%", display:"flex", alignItems:"center", gap:".45rem", padding:".5rem .65rem", background:on?"var(--c-cyan-dim,rgba(0,212,255,.12))":"transparent", border:on?"1px solid rgba(0,212,255,.3)":"1px solid transparent", borderRadius:8, cursor:"pointer", color:on?"var(--c-cyan,#00d4ff)":"var(--c-text2,#7aa3c0)", fontSize:".75rem", fontWeight:600, textAlign:"left", marginBottom:2 }}>
                  <span>{pt.icon}</span>
                  <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lang==="es"?pt.labelEs:pt.label}</span>
                  <span style={{ fontSize:".6rem", opacity:.6, fontFamily:"var(--font-mono)" }}>{count}</span>
                </button>
              );
            })}
          </div>
          <div style={{ flex:1, overflowY:"auto", padding:"1rem" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:".75rem" }}>
              <div style={{ fontWeight:700, color:"var(--c-white,#eef6ff)", fontSize:".85rem" }}>
                {POLICY_TYPES.find(p=>p.id===selectedType)?.icon} {lang==="es"?POLICY_TYPES.find(p=>p.id===selectedType)?.labelEs:POLICY_TYPES.find(p=>p.id===selectedType)?.label}
              </div>
              <button onClick={resetType} style={{ background:"none", border:"1px solid var(--c-border,#1a3550)", color:"var(--c-text3,#45688a)", padding:".3rem .65rem", borderRadius:6, fontSize:".68rem", cursor:"pointer" }}>↩ {lang==="es"?"Restaurar":"Reset"}</button>
            </div>
            {docs.length===0 && (
              <div style={{ textAlign:"center", padding:"1.5rem", color:"var(--c-text3,#45688a)", fontSize:".8rem" }}>{lang==="es"?"No hay documentos. Agrega uno:":"No documents. Add one:"}</div>
            )}
            {docs.map((d, i) => (
              <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto auto auto", gap:".5rem", marginBottom:".5rem", alignItems:"center" }}>
                <input value={d.name||""} onChange={e=>updateDoc(i,"name",e.target.value)} placeholder={lang==="es"?"Nombre (EN)":"Name (EN)"} style={{ background:"var(--c-surface2,#112538)", border:"1px solid var(--c-border,#1a3550)", borderRadius:7, padding:".45rem .65rem", color:"var(--c-text,#c8dff0)", fontSize:".78rem", outline:"none" }}/>
                <input value={d.nameEs||""} onChange={e=>updateDoc(i,"nameEs",e.target.value)} placeholder={lang==="es"?"Nombre (ES)":"Name (ES)"} style={{ background:"var(--c-surface2,#112538)", border:"1px solid var(--c-border,#1a3550)", borderRadius:7, padding:".45rem .65rem", color:"var(--c-text,#c8dff0)", fontSize:".78rem", outline:"none" }}/>
                <button type="button" onClick={()=>translateName(i)} disabled={trRow===i} title={lang==="es"?"Traducir con IA al otro idioma":"AI-translate to the other language"} style={{ background:"rgba(0,255,136,.08)", border:"1px solid rgba(0,255,136,.25)", color:"#00ff88", cursor:trRow===i?"wait":"pointer", padding:".3rem .5rem", borderRadius:6, fontSize:".7rem", fontWeight:700 }}>{trRow===i?"…":"🤖"}</button>
                <label style={{ display:"flex", alignItems:"center", gap:".3rem", fontSize:".7rem", color:"var(--c-text2,#7aa3c0)", cursor:"pointer" }}>
                  <input type="checkbox" checked={!!d.required} onChange={e=>updateDoc(i,"required",e.target.checked)}/>
                  {lang==="es"?"Req":"Req"}
                </label>
                <button onClick={()=>removeDoc(i)} style={{ background:"none", border:"none", color:"var(--c-text3,#45688a)", cursor:"pointer", padding:4 }} onMouseEnter={e=>e.currentTarget.style.color="#ef4444"} onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}><Trash2 size={14}/></button>
              </div>
            ))}
            <button onClick={addDoc} style={{ display:"flex", alignItems:"center", gap:".35rem", background:"var(--c-surface2,#112538)", border:"1px dashed var(--c-border2,#1f3f60)", color:"var(--c-text2,#7aa3c0)", padding:".5rem .75rem", borderRadius:8, fontSize:".75rem", cursor:"pointer", marginTop:".5rem" }}>
              <Plus size={12}/> {lang==="es"?"Agregar documento":"Add document"}
            </button>
          </div>
        </div>
        <div style={{ padding:"1rem 1.25rem", borderTop:"1px solid var(--c-border,#1a3550)", display:"flex", gap:".5rem", justifyContent:"space-between" }}>
          <button onClick={resetAll} style={{ background:"none", border:"1px solid rgba(239,68,68,.3)", color:"#ef4444", padding:".55rem 1rem", borderRadius:8, fontSize:".78rem", cursor:"pointer", fontWeight:600 }}>↩ {lang==="es"?"Restaurar Todo":"Reset All"}</button>
          <div style={{ display:"flex", gap:".5rem" }}>
            <button onClick={onClose} style={{ background:"var(--c-surface2,#112538)", border:"1px solid var(--c-border,#1a3550)", color:"var(--c-text2,#7aa3c0)", padding:".55rem 1rem", borderRadius:8, fontSize:".78rem", cursor:"pointer", fontWeight:600 }}>{lang==="es"?"Cancelar":"Cancel"}</button>
            <button onClick={()=>{onSave(local);onClose();}} style={{ background:"linear-gradient(135deg,#0099cc,#0055aa)", border:"none", color:"#fff", padding:".55rem 1.2rem", borderRadius:8, fontSize:".78rem", cursor:"pointer", fontWeight:700 }}>{lang==="es"?"Guardar":"Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DocRequests({ requests, onRequestsChange, clients, onClientsChange, policies, docTemplatesMaster, onDocTemplatesMasterChange, uploadsBump, currentUser, hasTeamActionBeenDone, logTeamAction, confirmBeforeResend, onOpenClient, t, lang }) {
  const dr = t.docRequests;
  const isApiModeDR = typeof window !== "undefined" && !!window.__deskteAuth;
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formClientId, setFormClientId] = useState("");
  const [formDocs, setFormDocs] = useState([{name:"", required:true}]);
  const [formChannel, setFormChannel] = useState("email"); // email | sms | whatsapp
  // Manual recipient: when the person isn't in the CRM, type their name/email
  // and we auto-create a prospect on send (Lissette 2026-06-18).
  const [manualMode, setManualMode] = useState(false);
  const [manualFirst, setManualFirst] = useState("");
  const [manualLast, setManualLast] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualPhone, setManualPhone] = useState("");
  // Language of the email/text the client receives, and an optional custom intro
  // message (empty = use the default bilingual template). Defaults to the
  // selected client's preferred language (Lissette 2026-06-18).
  const [formLang, setFormLang] = useState(lang === "es" ? "es" : "en");
  // Pre-filled with the saved/standard text for the current language; editable.
  const [formMessage, setFormMessage] = useState(() => loadDocMsg(lang === "es" ? "es" : "en"));
  // Insert a {{merge}} field at the cursor of the message box. This message is
  // SAVED and reused per language, so we insert a token (not a literal name) and
  // the backend fills it per-recipient at send time.
  const insertDocVar = (tag) => {
    const el = document.getElementById('docreq-msg');
    setFormMessage((prev) => {
      if (!el || typeof el.selectionStart !== 'number') return prev + tag;
      const s = el.selectionStart, e = el.selectionEnd;
      setTimeout(() => { try { el.focus(); el.selectionStart = el.selectionEnd = s + tag.length; } catch {} }, 40);
      return prev.slice(0, s) + tag + prev.slice(e);
    });
  };
  const [formTranslating, setFormTranslating] = useState(false);
  // ES/EN toggle: AI-translate the message INTO the chosen language in place,
  // preserving the {{campos}}. Saves the current text under the old language
  // first (so the per-language memory is kept). Active language = no-op.
  const translateFormTo = async (to) => {
    const from = formLang === 'es' ? 'es' : 'en';
    if (to === from || formTranslating) return;
    try { prefsSet('deskte:docReqMsg_' + from, formMessage); } catch {}
    if (!(formMessage || '').trim()) { setFormLang(to); setFormMessage(loadDocMsg(to)); return; }
    setFormTranslating(true);
    try {
      const api = await import('../api.js');
      const { translated } = await api.aiTranslateTemplate({ text: formMessage, fromLang: from, toLang: to });
      if (translated && translated.trim()) setFormMessage(translated);
      setFormLang(to);
    } catch (e) { toast.error((lang === 'es' ? 'No se pudo traducir: ' : 'Translation failed: ') + (e.message || '')); }
    finally { setFormTranslating(false); }
  };
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showTemplatesEditor, setShowTemplatesEditor] = useState(false);
  const [viewUploadsFor, setViewUploadsFor] = useState(null);
  // M1: uploaded-file listing now comes from the backend (Document rows tagged
  // with documentRequestId), not the old localStorage cache. uploadsByRequest
  // holds the per-request file list for the count strip; modalFiles holds the
  // open "View files" modal's files with a freshly-minted read URL each.
  const [uploadsByRequest, setUploadsByRequest] = useState({});
  const [modalFiles, setModalFiles] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const requestIdsKey = (requests || []).map(r => r.id).join(',');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all((requests || []).map(async (req) => {
        try { return [req.id, await listUploadsForRequest(req.id)]; }
        catch { return [req.id, []]; }
      }));
      if (!cancelled) setUploadsByRequest(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [requestIdsKey, uploadsBump]);
  useEffect(() => {
    if (!viewUploadsFor) { setModalFiles([]); return; }
    let cancelled = false;
    setModalLoading(true);
    (async () => {
      try {
        const files = await listUploadsForRequest(viewUploadsFor);
        // Mint a short-lived read URL per file so previews/downloads work
        // against the private blob container (same SAS path the Documents tab
        // uses — no new auth surface).
        const withUrls = await Promise.all(files.map(async (f) => {
          let viewUrl = null;
          try { viewUrl = await getDocumentReadUrl(f.id); } catch { /* leave null → no preview */ }
          return { ...f, viewUrl };
        }));
        if (!cancelled) setModalFiles(withUrls);
      } catch { if (!cancelled) setModalFiles([]); }
      finally { if (!cancelled) setModalLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [viewUploadsFor]);
  const downloadUpload = async (id) => {
    try {
      const url = await getDocumentReadUrl(id, { download: true });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error((lang === 'es' ? 'No se pudo descargar: ' : "Couldn't download: ") + e.message);
    }
  };

  const clientPolicyTypes = useMemo(() => {
    if (!formClientId) return [];
    return [...new Set((policies||[]).filter(p=>p.clientId===formClientId).map(p=>p.type))];
  }, [formClientId, policies]);

  const addDocsFromPolicyType = (policyType) => {
    const master = docTemplatesMaster || REQUIRED_DOCS_BY_POLICY_DEFAULT;
    const toAdd = (master[policyType] || REQUIRED_DOCS_BY_POLICY_DEFAULT[policyType] || []).map(d => ({
      name: lang==="es" ? (d.nameEs || d.name) : d.name,
      // Every doc shown in the request is "Requerido" — no per-doc checkbox
      // anymore; the agent removes the ones she doesn't want with the X
      // (Lissette 2026-06-23).
      required: true
    }));
    setFormDocs(prev => {
      const existing = new Set(prev.filter(d=>d.name).map(d=>d.name.toLowerCase().trim()));
      const merged = [...prev.filter(d=>d.name)];
      toAdd.forEach(d => {
        if (!existing.has(d.name.toLowerCase().trim())) {
          merged.push(d);
          existing.add(d.name.toLowerCase().trim());
        }
      });
      return merged.length ? merged : [{name:"",required:true}];
    });
  };

  const handleClientSelect = (cid) => {
    setFormClientId(cid);
    if (cid) {
      // Default the email language to the client's preferred language.
      const c = (clients || []).find(x => x.id === cid);
      const pl = (c?.preferredLang || c?.preferredLanguage || "").toLowerCase();
      if (pl) {
        // Picking a client sets the initial message language to their preference
        // and LOADS that language's saved text (no AI translate — that's only on
        // the ES/EN buttons). Mirrors the old switchFormLang behavior.
        const target = pl.startsWith("es") || pl.startsWith("spa") ? "es" : "en";
        try { prefsSet('deskte:docReqMsg_' + formLang, formMessage); } catch {}
        setFormLang(target);
        setFormMessage(loadDocMsg(target));
      }
    }
    // Don't auto-fill docs from the client's policies — Lissette found it
    // confusing that ALL docs for every policy appeared at once. She wants the
    // suggested docs to appear only WHEN she picks a policy type (the chips /
    // dropdown below). So just reset to one empty row (2026-06-23).
    setFormDocs([{name:"",required:true}]);
  };

  const getRequestStatus = (req) => {
    const docs = req.docs || [];
    const allReceivedLocal = docs.length > 0 && docs.every(d => d.status === "received");
    const hasOverdueLocal = docs.some(d => d.status === "overdue");
    // The backend tracks fulfillment by upload count and flips the request
    // status pending → partial → completed (or expired) as the client uploads
    // through the portal. That backend status is authoritative; the synthesized
    // per-doc `status` is always 'pending' on hydration, so relying on it alone
    // left fulfilled requests stuck showing "pending". Honor whichever signal
    // says "done": backend `completed` OR every doc manually marked received.
    if (req.status === "completed" || allReceivedLocal) return "received";
    if (req.status === "expired" || hasOverdueLocal) return "overdue";
    // 'partial' (some uploaded) and 'pending' both still await documents.
    return "pending";
  };

  const STATUS_MAP = {
    all:      { label: lang === "es" ? "Todas" : "All", color: "#7aa3c0" },
    pending:  { label: dr.pending, color: "#ffb700", bg: "rgba(255,183,0,.12)" },
    received: { label: dr.received, color: "#00ff88", bg: "rgba(0,255,136,.12)" },
    overdue:  { label: dr.overdue, color: "#ff3d6b", bg: "rgba(255,61,107,.12)" },
  };

  const filtered = requests.filter(r => {
    const rs = getRequestStatus(r);
    if (statusFilter !== "all" && rs !== statusFilter) return false;
    if (search) {
      const c = clients.find(cl => cl.id === r.clientId);
      const name = c ? `${c.firstName} ${c.lastName}` : "";
      const docNames = (r.docs || []).map(d => d.name).join(" ");
      return `${name} ${docNames} ${r.email}`.toLowerCase().includes(search.toLowerCase());
    }
    return true;
  });

  const toggleDocStatus = (reqId, docIdx) => {
    onRequestsChange(prev => prev.map(r => {
      if (r.id !== reqId) return r;
      const newDocs = [...r.docs];
      const cur = newDocs[docIdx].status;
      newDocs[docIdx] = { ...newDocs[docIdx], status: cur === "received" ? "pending" : "received" };
      return { ...r, docs: newDocs };
    }));
  };

  const markDocOverdue = (reqId, docIdx) => {
    onRequestsChange(prev => prev.map(r => {
      if (r.id !== reqId) return r;
      const newDocs = [...r.docs];
      newDocs[docIdx] = { ...newDocs[docIdx], status: newDocs[docIdx].status === "overdue" ? "pending" : "overdue" };
      return { ...r, docs: newDocs };
    }));
  };

  const deleteRequest = async (reqId) => {
    if (isApiModeDR) {
      try {
        const { deleteDocRequest } = await import("../api.js");
        await deleteDocRequest(reqId);
      } catch (err) {
        toast.error((lang === 'es' ? 'No se pudo eliminar: ' : 'Delete failed: ') + err.message);
        return;
      }
    }
    onRequestsChange(prev => prev.filter(r => r.id !== reqId));
  };

  const copyLink = (link, id) => {
    navigator.clipboard.writeText(link).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // El SMS sale POR DESKTE (Quo / Twilio, el proveedor conectado del agente):
  // queda en el hilo del cliente, respeta la lista de No-Textear y llega desde
  // el numero de la agencia. Antes se abria la app de mensajes del ordenador y
  // el SMS salia —si salia— del telefono personal de quien estuviera sentado
  // ahi (Lissette, 2026-09-01). Ese enlace sigue existiendo como PLAN B dentro
  // de `enviarSmsAlCliente`, ya diciendo por que se cayo a el.
  //
  // WhatsApp se queda con su enlace: no hay proveedor de WhatsApp conectado,
  // wa.me abre la cuenta de la agente y ahi es la via correcta.
  const handoffViaDeepLink = async (channel, client, portalUrl, title) => {
    const phone = (client?.phone || "").replace(/[^0-9+]/g, "");
    const intro = lang === 'es'
      ? `Hola ${client?.firstName || ''}, por favor sube los documentos aquí: ${portalUrl}`
      : `Hi ${client?.firstName || ''}, please upload the requested documents here: ${portalUrl}`;
    if (channel === 'sms') {
      const r = await enviarSmsAlCliente({ clientId: client?.id, telefono: client?.phone, mensaje: intro, lang });
      if (r.enviado) toast.success(lang === 'es' ? `SMS enviado a ${client?.phone}` : `SMS sent to ${client?.phone}`, { link: portalUrl });
      else if (r.texto) toast.warn(r.texto, { link: portalUrl });
    } else if (channel === 'whatsapp' && phone) {
      const wa = phone.startsWith('+') ? phone.replace('+', '') : (phone.startsWith('1') ? phone : '1' + phone);
      window.open(`https://wa.me/${wa}?text=${encodeURIComponent(intro)}`, '_blank', 'noopener,noreferrer');
    }
  };

  const createRequest = async () => {
    // Remember the message per language so it's pre-filled next time.
    try { prefsSet('deskte:docReqMsg_' + formLang, formMessage); } catch {}
    const validDocs = formDocs.filter(d => d.name && d.name.trim());
    if (validDocs.length === 0) return;
    if (manualMode ? !manualFirst.trim() : !formClientId) return;
    if (submitting) return;
    setSubmitting(true);

    let client = clients.find(c => c.id === formClientId);
    let clientId = formClientId;

    if (isApiModeDR) {
      try {
        const { createDocRequest, createClient } = await import("../api.js");
        // Recipient not in the CRM → create a prospect first, then request for it.
        if (manualMode) {
          const prospect = await createClient({
            firstName: manualFirst.trim(),
            lastName: manualLast.trim(),
            email: manualEmail.trim(),
            phone: manualPhone.trim(),
            status: 'prospect',
          });
          client = prospect;
          clientId = prospect.id;
          onClientsChange && onClientsChange(prev => [...(prev || []), prospect]);
        }
        const recipientEmail = (client?.email || manualEmail || '').trim() || null;
        // PHI_DETECTED no es una negativa, es una PREGUNTA del servidor:
        // "revisa esto y reenvia confirmando". Sin contestarla, la solicitud
        // no se podia mandar de ninguna forma.
        const crearSolicitud = (ack) => createDocRequest({
          clientId,
          title: `${(client?.firstName || '')} ${(client?.lastName || '')}`.trim() || 'Document Request',
          documentsNeeded: validDocs.map(d => d.name.trim()),
          channel: formChannel,
          recipientEmail,
          expirationDays: 14,
          language: formLang,
          message: formMessage.trim() || undefined,
          acknowledgePhi: ack,
        });
        let created;
        try {
          created = await crearSolicitud(false);
        } catch (err) {
          const motivo = mensajeDeFalloDeSolicitud(err, lang);
          if (!motivo.sePuedeConfirmar) throw err;
          const ok = await confirmDialog({
            title: lang === 'es' ? 'Posible información sensible' : 'Possible sensitive info',
            message: motivo.texto,
            danger: true,
            confirmLabel: lang === 'es' ? 'Enviar igual' : 'Send anyway',
          });
          if (!ok) { setSubmitting(false); return; }
          created = await crearSolicitud(true);
        }
        const portalUrl = created.portalUrl || (window.location.origin + '/upload/' + created.portalToken);
        // La fila se arma con el MISMO armador que la lista (queja 76): esta
        // copia suelta no ponia `clientName` ni `title`, asi que la solicitud
        // recien creada salia con "—" en Cliente hasta refrescar la pagina.
        // Los documentos SI vienen del formulario, que es lo unico que sabe
        // cual era obligatorio.
        const newReq = filaDeSolicitud(created, {
          origen: window.location.origin,
          docs: validDocs.map(d => ({ name: d.name.trim(), required: !!d.required, status: 'pending' })),
        });
        onRequestsChange(prev => [...prev, newReq]);

        if (formChannel === 'email') {
          if (created.emailSent) {
            toast.success(lang === 'es' ? `Email enviado a ${recipientEmail}` : `Email sent to ${recipientEmail}`, { link: portalUrl });
          } else {
            // NO se culpa a ACS. Las solicitudes salen del buzon del AGENTE
            // (resolveSendingMailbox), asi que "ACS no respondio" era una
            // explicacion de otro tiempo — el mismo aviso que mentia en las
            // firmas y que se corrigio el 2026-08-29 (ticket de Nina).
            toast.warn(lang === 'es'
              ? 'El correo no salió. El enlace ya está copiado — puedes mandarlo tú por WhatsApp o SMS.'
              : "The email didn't go out. The link is already copied — you can send it yourself by WhatsApp or SMS.", { link: portalUrl });
          }
          try { await navigator.clipboard.writeText(portalUrl); } catch {}
        } else {
          await handoffViaDeepLink(formChannel, client, portalUrl, newReq.docs.map(d => d.name).join(', '));
        }

        if (logTeamAction) logTeamAction("doc_request_sent", clientId, { requestId: created.id });
      } catch (err) {
        toast.error(mensajeDeFalloDeSolicitud(err, lang).texto);
        setSubmitting(false);
        return;
      }
    } else {
      // Demo mode (no backend) — keep the previous local-only flow.
      const token = "tk_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      const base = window.location.origin + window.location.pathname;
      onRequestsChange(prev => [...prev, {
        id: "r_" + Date.now(),
        clientId: formClientId,
        docs: validDocs.map(d => ({ name: d.name.trim(), required: !!d.required, status: 'pending' })),
        sentAt: todayLocal(),
        uploadToken: token,
        link: base + "#upload/" + token,
        email: client?.email || '',
      }]);
    }

    setSubmitting(false);
    setShowForm(false);
    setFormClientId("");
    setFormDocs([{ name: "", required: true }]);
    setFormChannel("email");
    setFormMessage(loadDocMsg(formLang));
    setManualMode(false);
    setManualFirst(""); setManualLast(""); setManualEmail(""); setManualPhone("");
  };

  const DOC_STATUS_ICON = {
    pending: { icon: "⏳", color: "#ffb700", bg: "rgba(255,183,0,.1)", border: "rgba(255,183,0,.2)" },
    received: { icon: "✅", color: "#00ff88", bg: "rgba(0,255,136,.08)", border: "rgba(0,255,136,.2)" },
    overdue: { icon: "🔴", color: "#ff3d6b", bg: "rgba(255,61,107,.08)", border: "rgba(255,61,107,.2)" },
  };

  return (
    <div>
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
          <span style={{ fontSize: "1.4rem" }}>📋</span>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--c-white,#eef6ff)", margin: 0 }}>{dr.title}</h2>
          <span style={{ fontSize: ".75rem", color: "var(--c-text3,#45688a)", background: "var(--c-surface2,#112538)", padding: "2px 10px", borderRadius: 100, fontWeight: 700, border: "1px solid var(--c-border,#1a3550)" }}>{requests.length}</span>
        </div>
        <div style={{ display:"flex", gap:".4rem" }}>
          <button type="button" onClick={()=>{
            const data = requests.map(r => {
              const c = clients.find(cl=>cl.id===r.clientId);
              return {
                Client: c ? `${c.firstName} ${c.lastName}` : "Unknown",
                Email: r.email||"",
                Documents: (r.docs||[]).map(d=>d.name).join("; "),
                Status: (r.docs||[]).map(d=>`${d.name}: ${d.status}`).join("; "),
                SentAt: r.sentAt||"",
                Link: r.link||"",
              };
            });
            exportToExcel(data, "deskte-doc-requests.xlsx", "Requests");
          }} style={{ display:"flex", alignItems:"center", gap:4, background:"var(--c-surface2,#112538)", border:"1px solid var(--c-border,#1a3550)", color:"var(--c-text2,#7aa3c0)", fontSize:".72rem", fontWeight:700, padding:".5rem .75rem", borderRadius:9, cursor:"pointer", fontFamily:"inherit" }}>
            <Download size={13}/> Excel
          </button>
          <button type="button" onClick={()=>setShowTemplatesEditor(true)} title={lang==="es"?"Plantillas":"Templates"}
            style={{ display:"flex", alignItems:"center", gap:4, background:"var(--c-surface2,#112538)", border:"1px solid var(--c-border,#1a3550)", color:"var(--c-text2,#7aa3c0)", fontSize:".72rem", fontWeight:700, padding:".5rem .75rem", borderRadius:9, cursor:"pointer", fontFamily:"inherit" }}>
            <Edit2 size={13}/> {lang==="es"?"Plantillas":"Templates"}
          </button>
          <button type="button" onClick={() => setShowForm(true)}
            style={{ display: "flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".78rem", fontWeight: 700, padding: ".5rem 1rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
            <Plus size={14} />{dr.newRequest}
          </button>
        </div>
      </div>
      {showTemplatesEditor && <DocTemplatesEditor master={docTemplatesMaster} onSave={onDocTemplatesMasterChange} onClose={()=>setShowTemplatesEditor(false)} lang={lang}/>}

      {/* STATUS FILTERS */}
      <div style={{ display: "flex", gap: ".35rem", marginBottom: ".75rem", flexWrap: "wrap" }}>
        {Object.entries(STATUS_MAP).map(([key, s]) => {
          const count = key === "all" ? requests.length : requests.filter(r => getRequestStatus(r) === key).length;
          return (
            <button type="button" key={key} onClick={() => setStatusFilter(key)}
              style={{ padding: ".3rem .7rem", borderRadius: 100, border: `1px solid ${statusFilter === key ? s.color + "55" : "var(--c-border,#1a3550)"}`, background: statusFilter === key ? (s.bg || "var(--c-surface2,#112538)") : "transparent", color: statusFilter === key ? s.color : "var(--c-text3,#45688a)", fontSize: ".7rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}>
              {s.label} ({count})
            </button>
          );
        })}
      </div>

      {/* SEARCH */}
      <div style={{ position: "relative", marginBottom: ".75rem" }}>
        <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--c-text3,#45688a)" }} />
        <input className="dkt-inp" placeholder={lang === "es" ? "Buscar solicitudes..." : "Search requests..."} value={search} onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft: 32, width: "100%", boxSizing: "border-box" }} />
      </div>

      {/* LIST */}
      {filtered.length === 0 && <div style={{ textAlign: "center", padding: "2.5rem", color: "var(--c-text3,#45688a)", fontSize: ".82rem" }}>{dr.noRequests}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
        {filtered.map(req => {
          const c = clients.find(cl => cl.id === req.clientId);
          const rs = getRequestStatus(req);
          const rsInfo = STATUS_MAP[rs] || STATUS_MAP.pending;
          const reqUploads = uploadsByRequest[req.id] || [];
          return (
            <div key={req.id} style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderLeft: `3px solid ${rsInfo.color}`, borderRadius: 10, padding: ".875rem 1rem" }}>
              {/* Request header */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: ".65rem" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: ".88rem" }}>
                    {c ? (
                      onOpenClient ? (
                        // Click the client's name → open their ficha (client detail).
                        // Mirrors the "open client" link used in Sheets/Tasks/Month
                        // views: cyan + external-link icon so it reads as a link
                        // (Lissette 2026-07-30).
                        <button type="button" onClick={() => onOpenClient(c)}
                          title={lang === "es" ? "Abrir ficha del cliente" : "Open client profile"}
                          style={{ background: "none", border: "none", padding: 0, margin: 0, font: "inherit", fontWeight: 700, fontSize: ".88rem", color: "var(--c-cyan,#00d4ff)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, textAlign: "left" }}>
                          {c.firstName} {c.lastName}
                          <ExternalLink size={12} style={{ opacity: .75, flexShrink: 0 }} />
                        </button>
                      ) : (
                        <span style={{ color: "var(--c-white,#eef6ff)" }}>{c.firstName} {c.lastName}</span>
                      )
                    ) : (
                      <span style={{ color: "var(--c-white,#eef6ff)" }}>{lang === "es" ? "Cliente desconocido" : "Unknown client"}</span>
                    )}
                  </div>
                  <div style={{ fontSize: ".7rem", color: "var(--c-text3,#45688a)", marginTop: 2, display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
                    <span>📅 {lang === "es" ? "Enviado" : "Sent"}: {req.sentAt}</span>
                    {req.email && <span>📧 {req.email}</span>}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: ".35rem", flexShrink: 0 }}>
                  <span style={{ fontSize: ".65rem", padding: ".2rem .55rem", borderRadius: 100, fontWeight: 700, background: rsInfo.bg, color: rsInfo.color, border: `1px solid ${rsInfo.color}33` }}>
                    {rsInfo.label}
                  </span>
                  <button type="button" onClick={() => copyLink(req.link, req.id)}
                    style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: copiedId === req.id ? "var(--c-green,#00ff88)" : "var(--c-text3,#45688a)", cursor: "pointer", padding: "4px 8px", borderRadius: 7, lineHeight: 0, display: "flex", alignItems: "center", gap: 4, fontSize: ".62rem", fontWeight: 600, fontFamily: "inherit" }}>
                    {copiedId === req.id ? <><Check size={11} />{lang === "es" ? "Copiado" : "Copied"}</> : <><Copy size={11} />{dr.copyLink}</>}
                  </button>
                  <button type="button" onClick={async () => { if (await confirmDialog({ message: lang === "es" ? "¿Eliminar solicitud?" : "Delete request?", danger: true })) deleteRequest(req.id); }}
                    style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text3,#45688a)", cursor: "pointer", padding: "4px 7px", borderRadius: 7, lineHeight: 0 }}><Trash2 size={12} /></button>
                </div>
              </div>

              {/* Individual docs */}
              <div style={{ display: "flex", flexDirection: "column", gap: ".35rem" }}>
                {(req.docs || []).map((doc, di) => {
                  const ds = DOC_STATUS_ICON[doc.status] || DOC_STATUS_ICON.pending;
                  return (
                    <div key={di} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: ".45rem .65rem", borderRadius: 8, background: ds.bg, border: `1px solid ${ds.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: ".45rem" }}>
                        <span style={{ fontSize: ".82rem" }}>{ds.icon}</span>
                        <span style={{ fontSize: ".78rem", fontWeight: 600, color: "var(--c-white,#eef6ff)" }}>{doc.name}</span>
                        <span style={{ fontSize: ".6rem", fontWeight: 700, color: ds.color, textTransform: "uppercase", fontFamily: "var(--font-mono)" }}>
                          {doc.status === "pending" ? dr.pending : doc.status === "received" ? dr.received : dr.overdue}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: ".25rem" }}>
                        <button type="button" onClick={() => toggleDocStatus(req.id, di)}
                          title={doc.status === "received" ? (lang === "es" ? "Marcar pendiente" : "Mark pending") : (lang === "es" ? "Marcar recibido" : "Mark received")}
                          style={{ background: "none", border: `1px solid ${doc.status === "received" ? "rgba(0,255,136,.3)" : "rgba(255,183,0,.3)"}`, cursor: "pointer", padding: "3px 8px", borderRadius: 6, fontSize: ".6rem", fontWeight: 700, color: doc.status === "received" ? "var(--c-green,#00ff88)" : "var(--c-amber,#ffb700)", fontFamily: "inherit", transition: "all .15s" }}>
                          {doc.status === "received" ? "↩️" : "✅"}
                        </button>
                        {doc.status !== "received" && (
                          <button type="button" onClick={() => markDocOverdue(req.id, di)}
                            title={doc.status === "overdue" ? (lang === "es" ? "Quitar vencido" : "Remove overdue") : (lang === "es" ? "Marcar vencido" : "Mark overdue")}
                            style={{ background: "none", border: `1px solid rgba(255,61,107,.3)`, cursor: "pointer", padding: "3px 8px", borderRadius: 6, fontSize: ".6rem", fontWeight: 700, color: doc.status === "overdue" ? "var(--c-amber,#ffb700)" : "var(--c-red,#ff3d6b)", fontFamily: "inherit" }}>
                            {doc.status === "overdue" ? "⏳" : "🔴"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Uploaded files strip */}
              {reqUploads.length > 0 && (
                <div style={{ marginTop: ".5rem", display:"flex", alignItems:"center", gap:".5rem", padding:".45rem .65rem", background:"rgba(0,255,136,.06)", border:"1px solid rgba(0,255,136,.2)", borderRadius:8 }}>
                  <Paperclip size={12} style={{ color:"var(--c-green,#00ff88)" }}/>
                  <span style={{ fontSize:".72rem", fontWeight:700, color:"var(--c-green,#00ff88)" }}>
                    {reqUploads.length} {lang==="es"?"archivo(s) recibido(s)":"file(s) received"}
                  </span>
                  <button type="button" onClick={()=>setViewUploadsFor(req.id)} style={{ marginLeft:"auto", background:"var(--c-surface3,#162d44)", border:"1px solid rgba(0,255,136,.3)", color:"var(--c-green,#00ff88)", cursor:"pointer", padding:"3px 10px", borderRadius:6, fontSize:".65rem", fontWeight:700, fontFamily:"inherit" }}>
                    <Eye size={10} style={{ display:"inline", marginRight:3 }}/>{lang==="es"?"Ver":"View"}
                  </button>
                </div>
              )}
              {/* Upload link hint */}
              <div style={{ marginTop: ".5rem", fontSize: ".65rem", color: "var(--c-text3,#45688a)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                <Link2 size={11} />
                <span style={{ fontFamily: "var(--font-mono)", fontSize: ".6rem", color: "var(--c-cyan,#00d4ff)", opacity: .7, wordBreak:"break-all" }}>{req.link}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* VIEW UPLOADS MODAL */}
      {viewUploadsFor && (() => {
        const req = requests.find(r => r.id === viewUploadsFor);
        const c = req ? clients.find(cl => cl.id === req.clientId) : null;
        return (
          <div style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(0,0,0,.85)", display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }} onClick={()=>setViewUploadsFor(null)}>
            <div onClick={e=>e.stopPropagation()} style={{ background:"var(--c-surface,#0d1c2e)", border:"1px solid var(--c-border,#1a3550)", borderRadius:14, padding:"1.25rem", maxWidth:700, width:"100%", maxHeight:"85vh", overflow:"auto" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1rem" }}>
                <div>
                  <div style={{ fontWeight:800, fontSize:".95rem", color:"var(--c-white,#eef6ff)" }}>📎 {lang==="es"?"Archivos Subidos":"Uploaded Files"}</div>
                  <div style={{ fontSize:".7rem", color:"var(--c-text3,#45688a)", marginTop:2 }}>{c ? `${c.firstName} ${c.lastName}` : ""}</div>
                </div>
                <button type="button" onClick={()=>setViewUploadsFor(null)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--c-text3,#45688a)" }}><X size={18}/></button>
              </div>
              {modalLoading && <div style={{ textAlign:"center", padding:"2rem", color:"var(--c-text3,#45688a)", fontSize:".85rem" }}>{lang==="es"?"Cargando...":"Loading..."}</div>}
              {!modalLoading && modalFiles.length === 0 && <div style={{ textAlign:"center", padding:"2rem", color:"var(--c-text3,#45688a)", fontSize:".85rem" }}>{lang==="es"?"No hay archivos":"No files"}</div>}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:".75rem" }}>
                {modalFiles.map(f => (
                    <div key={f.id} style={{ background:"var(--c-surface2,#112538)", border:"1px solid var(--c-border,#1a3550)", borderRadius:10, overflow:"hidden" }}>
                      {f.isImage && f.viewUrl ? (
                        <img src={f.viewUrl} alt={f.name} style={{ width:"100%", height:140, objectFit:"cover", display:"block" }}/>
                      ) : f.isHeic ? (
                        // Photo straight off an iPhone. The bytes are fine, but no
                        // browser renders HEIC and Windows needs a free Store codec —
                        // say so here instead of showing an empty box.
                        <div style={{ height:140, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:".3rem", padding:".5rem", textAlign:"center", background:"rgba(255,183,0,.08)" }}>
                          <span style={{ fontSize:"2rem" }}>📱</span>
                          <div style={{ fontSize:".62rem", fontWeight:800, color:"var(--c-amber,#ffb700)" }}>{lang==="es"?"Foto de iPhone (HEIC)":"iPhone photo (HEIC)"}</div>
                          <div style={{ fontSize:".58rem", color:"var(--c-text3,#45688a)", lineHeight:1.3 }}>
                            {lang==="es"
                              ? "Descárgala. Si Windows no la abre, instala \"HEIF Image Extensions\" (gratis) desde Microsoft Store."
                              : "Download it. If Windows won't open it, install \"HEIF Image Extensions\" (free) from the Microsoft Store."}
                          </div>
                        </div>
                      ) : (
                        <div style={{ height:140, display:"flex", alignItems:"center", justifyContent:"center", background:"var(--c-surface3,#162d44)" }}>
                          <span style={{ fontSize:"3rem" }}>📄</span>
                        </div>
                      )}
                      <div style={{ padding:".5rem .65rem" }}>
                        {f.uploadedBy && <div style={{ fontSize:".65rem", color:"var(--c-text3,#45688a)", fontWeight:700, textTransform:"uppercase", marginBottom:2 }}>{f.uploadedBy}</div>}
                        <div style={{ fontSize:".72rem", color:"var(--c-white,#eef6ff)", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</div>
                        <div style={{ fontSize:".6rem", color:"var(--c-text3,#45688a)", marginTop:2 }}>{f.sizeStr} · {fmt(f.uploadedAt)}</div>
                        <button type="button" onClick={()=>downloadUpload(f.id)} style={{ display:"inline-block", marginTop:".35rem", fontSize:".62rem", color:"var(--c-cyan,#00d4ff)", background:"none", border:"none", cursor:"pointer", fontWeight:700, fontFamily:"inherit", padding:0 }}>
                          ⬇ {lang==="es"?"Descargar":"Download"}
                        </button>
                      </div>
                    </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* NEW REQUEST MODAL */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 14, padding: "1.5rem", maxWidth: 500, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "var(--c-white,#eef6ff)" }}>📋 {dr.newRequest}</div>
              <button type="button" onClick={() => setShowForm(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text3,#45688a)" }}><X size={18} /></button>
            </div>

            {/* Select client (from the CRM) OR enter a recipient manually when
                the person isn't in the CRM yet (auto-creates a prospect). */}
            <div style={{ marginBottom: ".75rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: ".5rem", flexWrap: "wrap" }}>
                <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>{manualMode ? (lang === "es" ? "Destinatario (manual)" : "Recipient (manual)") : dr.selectClient}</label>
                <button type="button" onClick={() => setManualMode(m => !m)} style={{ background: "none", border: "none", color: "var(--c-cyan,#00d4ff)", fontSize: ".68rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {manualMode ? (lang === "es" ? "← Buscar en el CRM" : "← Search the CRM") : (lang === "es" ? "¿No está en el CRM? Ingresar manual" : "Not in the CRM? Enter manually")}
                </button>
              </div>
              {!manualMode ? (
                <ClientSearchSelect clients={clients} value={formClientId} onChange={e => handleClientSelect(e.target.value)} lang={lang} noneLabel={`-- ${dr.selectClient} --`} />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
                  <div style={{ display: "flex", gap: ".4rem" }}>
                    <input className="dkt-inp" value={manualFirst} onChange={e => setManualFirst(e.target.value)} placeholder={lang === "es" ? "Nombre" : "First name"} style={{ flex: 1 }} />
                    <input className="dkt-inp" value={manualLast} onChange={e => setManualLast(e.target.value)} placeholder={lang === "es" ? "Apellido" : "Last name"} style={{ flex: 1 }} />
                  </div>
                  <input className="dkt-inp" type="email" value={manualEmail} onChange={e => setManualEmail(e.target.value)} placeholder={lang === "es" ? "Correo (para enviar el link)" : "Email (to send the link)"} />
                  <input className="dkt-inp" value={manualPhone} onChange={e => setManualPhone(e.target.value)} placeholder={lang === "es" ? "Teléfono (opcional, para SMS/WhatsApp)" : "Phone (optional, for SMS/WhatsApp)"} />
                  <div style={{ fontSize: ".64rem", color: "var(--c-text3,#45688a)" }}>{lang === "es" ? "Se creará como prospecto en tu CRM." : "Will be created as a prospect in your CRM."}</div>
                </div>
              )}
            </div>

            {/* Template docs by policy type: quick chips for the client's policy
                types + a dropdown to pull ANY type's template (so it works even
                for a prospect without that policy yet) — Lissette 2026-06-18. */}
            <div style={{ marginBottom: ".75rem" }}>
              <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".4rem", fontFamily: "var(--font-mono)" }}>{lang==="es"?"Agregar docs del template por tipo":"Add template docs by type"}</div>
              {clientPolicyTypes.length > 0 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:".3rem", marginBottom:".4rem" }}>
                  {clientPolicyTypes.map(pt => {
                    const ptInfo = POLICY_TYPES.find(p=>p.id===pt);
                    if (!ptInfo) return null;
                    return (
                      <button type="button" key={pt} onClick={()=>addDocsFromPolicyType(pt)} style={{ padding:".3rem .65rem", borderRadius:20, border:`1px solid ${ptInfo.color}`, background:`${ptInfo.color}22`, color:ptInfo.color, fontSize:".68rem", fontWeight:700, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:".3rem" }}>
                        <span>{ptInfo.icon}</span>
                        <span>+ {lang==="es"?ptInfo.labelEs:ptInfo.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <select className="dkt-inp" value="" onChange={(e)=>{ if (e.target.value) { addDocsFromPolicyType(e.target.value); e.target.value=""; } }} style={{ width:"100%", fontSize:".78rem", cursor:"pointer" }}>
                <option value="">{lang==="es"?"➕ Agregar del template de un tipo de seguro…":"➕ Add from an insurance type's template…"}</option>
                {POLICY_TYPES.map(p => <option key={p.id} value={p.id}>{p.icon} {lang==="es"?(p.labelEs||p.label):p.label}</option>)}
              </select>
            </div>

            {/* Documents to request */}
            <div style={{ marginBottom: ".75rem" }}>
              <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 6 }}>{lang === "es" ? "Documentos Solicitados" : "Documents Requested"}</label>
              <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
                {formDocs.map((doc, i) => (
                  <div key={i} style={{ display: "flex", gap: ".35rem", alignItems: "center" }}>
                    <input className="dkt-inp" value={doc.name||""} onChange={e => { const arr = [...formDocs]; arr[i] = {...arr[i], name: e.target.value}; setFormDocs(arr); }}
                      placeholder={`${dr.enterDocs} ${i + 1}`} style={{ flex: 1 }} />
                    {/* No more checkbox — every listed doc is simply "Requerido";
                        to drop one she just clicks the X (Lissette 2026-06-23). */}
                    {doc.name && doc.name.trim() && (
                      <span style={{ fontSize:".6rem", fontWeight:700, color:"var(--c-amber,#ffb700)", textTransform:"uppercase", letterSpacing:".04em", whiteSpace:"nowrap", padding:"0 .4rem" }}>
                        {lang==="es"?"Requerido":"Required"}
                      </span>
                    )}
                    {formDocs.length > 1 && (
                      <button type="button" title={lang==="es"?"Quitar":"Remove"} onClick={() => setFormDocs(prev => prev.filter((_, j) => j !== i))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text3,#45688a)", padding: 3 }}><X size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setFormDocs(prev => [...prev, {name:"",required:true}])}
                style={{ marginTop: ".4rem", background: "none", border: "1px dashed var(--c-border,#1a3550)", color: "var(--c-cyan,#00d4ff)", cursor: "pointer", padding: ".3rem .7rem", borderRadius: 7, fontSize: ".72rem", fontWeight: 600, fontFamily: "inherit", width: "100%" }}>
                + {lang === "es" ? "Agregar documento" : "Add document"}
              </button>
            </div>

            {/* Quick presets */}
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".4rem", fontFamily: "var(--font-mono)" }}>{lang === "es" ? "Presets rápidos" : "Quick presets"}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: ".3rem" }}>
                {[
                  lang === "es" ? "Verificación de Ingresos" : "Income Verification",
                  lang === "es" ? "Identificación Oficial" : "Government ID",
                  lang === "es" ? "Comprobante de Domicilio" : "Proof of Address",
                  lang === "es" ? "Declaración de Impuestos" : "Tax Return",
                  lang === "es" ? "Formulario de Consentimiento" : "Consent Form",
                  "SSN Card",
                  lang === "es" ? "Carta de Empleo" : "Employment Letter",
                  lang === "es" ? "Recibo de Nómina" : "Pay Stub",
                ].map(preset => (
                  <button type="button" key={preset} onClick={() => {
                    const idx = formDocs.findIndex(d => !d.name || !d.name.trim());
                    if (idx >= 0) { const arr = [...formDocs]; arr[idx] = {...arr[idx], name: preset, required: true}; setFormDocs(arr); }
                    else setFormDocs(prev => [...prev, {name:preset,required:true}]);
                  }}
                    style={{ padding: ".22rem .5rem", borderRadius: 6, border: "1px solid var(--c-border,#1a3550)", background: "var(--c-surface2,#112538)", color: "var(--c-text2,#7aa3c0)", fontSize: ".65rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}>
                    📄 {preset}
                  </button>
                ))}
              </div>
            </div>

            {/* Channel selector — pick how to deliver the upload link to the client */}
            <div style={{ marginBottom: ".75rem" }}>
              <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "var(--c-text3,#45688a)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".4rem" }}>
                {lang === "es" ? "Enviar el link por" : "Send the link via"}
              </label>
              <div style={{ display: "flex", gap: ".4rem" }}>
                {[
                  { id: "email", icon: "📧", label: "Email" },
                  { id: "sms", icon: "💬", label: "SMS" },
                  { id: "whatsapp", icon: "🟢", label: "WhatsApp" },
                ].map((ch) => {
                  const on = formChannel === ch.id;
                  const tone = ch.id === "whatsapp" ? "#25d366" : ch.id === "sms" ? "#818cf8" : "#00d4ff";
                  return (
                    <button key={ch.id} type="button" onClick={() => setFormChannel(ch.id)}
                      style={{ flex: 1, padding: ".5rem", borderRadius: 8, border: `1px solid ${on ? tone : "var(--c-border,#1a3550)"}`, background: on ? `${tone}1a` : "var(--c-surface2,#112538)", color: on ? tone : "var(--c-text3,#45688a)", fontSize: ".75rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                      {ch.icon} {ch.label}
                    </button>
                  );
                })}
              </div>
              {formChannel !== "email" && (
                <div style={{ fontSize: ".68rem", color: "var(--c-text3,#45688a)", marginTop: ".3rem" }}>
                  {lang === "es"
                    ? `Se abrirá tu app de ${formChannel === "sms" ? "Mensajes" : "WhatsApp"} con el texto pre-llenado para que tú lo envíes.`
                    : `Your ${formChannel === "sms" ? "Messages" : "WhatsApp"} app will open pre-filled — you send it.`}
                </div>
              )}
            </div>

            {/* Email language + optional custom message — the email the client
                receives is bilingual; default language follows the client. */}
            <div style={{ marginBottom: ".75rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".5rem", marginBottom: ".4rem" }}>
                <label style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--c-text3,#45688a)", textTransform: "uppercase", letterSpacing: ".5px" }}>
                  {lang === "es" ? "Idioma del mensaje (traduce con IA)" : "Message language (AI-translates)"}
                </label>
                <AiDraftButton lang={formLang === "es" ? "es" : "en"} compact
                  contextHint="This is a short message to an insurance client asking them to send/upload specific documents. Keep it brief and friendly. Address the reader with {{firstName}}. Return just the message body, no subject."
                  onResult={({ body }) => setFormMessage(body)} />
              </div>
              <div style={{ display: "flex", gap: ".4rem", marginBottom: ".5rem" }}>
                {[{ id: "es", label: "🇪🇸 ES" }, { id: "en", label: "🇺🇸 EN" }].map((L) => {
                  const on = formLang === L.id;
                  return (
                    <button key={L.id} type="button" onClick={() => translateFormTo(L.id)} disabled={formTranslating} title={lang === "es" ? "Traduce el mensaje a este idioma con IA" : "AI-translate the message into this language"}
                      style={{ flex: 1, padding: ".45rem", borderRadius: 8, border: `1px solid ${on ? "#00d4ff" : "var(--c-border,#1a3550)"}`, background: on ? "rgba(0,212,255,.1)" : "var(--c-surface2,#112538)", color: on ? "#00d4ff" : "var(--c-text3,#45688a)", fontSize: ".78rem", fontWeight: 800, cursor: formTranslating ? "wait" : "pointer", fontFamily: "inherit" }}>
                      {formTranslating && on ? "…" : L.label}
                    </button>
                  );
                })}
              </div>
              <textarea id="docreq-msg" value={formMessage} onChange={(e) => setFormMessage(e.target.value)} rows={3}
                placeholder={formLang === "es" ? "Mensaje del correo…" : "Email message…"}
                style={{ width: "100%", boxSizing: "border-box", background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 8, color: "var(--c-text,#c8dff0)", padding: ".5rem .6rem", fontSize: ".8rem", fontFamily: "inherit", resize: "vertical" }} />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginTop: ".4rem" }}>
                <span style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700 }}>🔗 {lang === "es" ? "Insertar:" : "Insert:"}</span>
                {[{ label: lang === "es" ? "Nombre" : "First name", tag: "{{firstName}}" }, { label: lang === "es" ? "Apellido" : "Last name", tag: "{{lastName}}" }, { label: lang === "es" ? "Mi nombre" : "My name", tag: "{{agentName}}" }, { label: lang === "es" ? "🔗 Portal" : "🔗 Portal", tag: "{{portalLink}}" }].map((ch) =>
                  <button type="button" key={ch.tag} onClick={() => insertDocVar(ch.tag)} title={ch.tag}
                    style={{ padding: "3px 9px", borderRadius: 8, border: "1px solid rgba(0,212,255,.3)", background: "rgba(0,212,255,.08)", color: "var(--c-cyan,#00d4ff)", fontSize: ".7rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{ch.label}</button>)}
              </div>
              <div style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", marginTop: ".3rem" }}>
                {lang === "es" ? "Este mensaje queda guardado por idioma — edítalo aquí y se recuerda para la próxima vez. Los campos se rellenan con los datos del cliente al enviar." : "This message is saved per language — edit it here and it's remembered next time. Fields fill with the client's data when sent."}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: ".5rem", justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setShowForm(false)} className="dkt-btn-s">{lang === "es" ? "Cancelar" : "Cancel"}</button>
              {(() => { const recipientMissing = manualMode ? !manualFirst.trim() : !formClientId; const disabled = submitting || recipientMissing || !formDocs.some(d => d.name && d.name.trim()); return (
              <button type="button" onClick={createRequest} disabled={disabled}
                style={{ display: "flex", alignItems: "center", gap: 5, background: disabled ? "#2a4560" : "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".78rem", fontWeight: 700, padding: ".5rem 1.2rem", borderRadius: 9, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                <Send size={13} />{submitting ? (lang === "es" ? "Enviando…" : "Sending…") : dr.sendRequest}
              </button>
              ); })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
